﻿import React, { Fragment, useState, useEffect, useRef } from 'react';
import { AlertUnsavedData, NotificationToaster } from "components/notifications";
import { ButtonSave, Button } from "components/buttons";
import { useDirtyData } from "hooks/useDirtyData";
import { UserBasicSettings } from "components/settings";
import { ShowHide } from 'components/layout';
import { ListingTable } from "components/listing";
import { FormSwitch } from "components/form-fields";
import { Alignment, Intent } from "@blueprintjs/core";

import PropTypes from 'prop-types';
import moment from "moment";
import _ from 'underscore';

export function ExternalContactSetting(
    {
        saving = false,
        editing = false,
        loading = true,
        externalContact,
        onExternalContactSave
    }
) {

    const externalContactModel = {
        emailAddress: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        secondaryPhoneNumber: "",
        verified: true,
        status: "",
        createDate: moment().utc().format("YYYY-MM-DD HH:mm"),
        locations: [],
        userType: "External",
        notificationUpcomingVisit: true,
        notificationUpcomingVisitAlteration: true,
        notificationReport: true,
        LocationExternalContactIdsToDelete: []
    };

    const [externalContactValid, setExternalContactValid] = useState(true);
    const [originalValues, setOriginalValues] = useState({});
    const [externalContactValue, setExternalContact] = useState(externalContactModel);
    const [tableHeaders] = useState(["Location Name", "Contact Type"]);
    const [tableRows, setTableRows] = useState([]);
    const inputRef = useRef(null);
    const isDataDirty = useDirtyData(originalValues, externalContactValue);

    useEffect(init, [loading]);

    function init() {
        if (loading) {
            return;
        }

        if (editing) {
            externalContact.LocationExternalContactIdsToDelete = [];
            setExternalContact(externalContact);
            setOriginalValues(_.extend({}, externalContact));
        } else {
            setExternalContact(externalContactModel);
            setOriginalValues(_.extend({}, externalContactModel));
            setTimeout(() => {
                if (inputRef.current != null) {
                    inputRef.current.focus();
                }
            });
        }
    }

    function onSettingsUpdate(userUpdate) {
        setExternalContact((prevExternalContact) => {
            return {
                ...prevExternalContact,
                emailAddress: userUpdate.emailAddress,
                firstName: userUpdate.firstName,
                lastName: userUpdate.lastName,
                phoneNumber: userUpdate.phoneNumber,
                secondaryPhoneNumber: userUpdate.secondaryPhoneNumber
            }
        });
    }

    function onExternalContactValidationChange(isValid) {
        setExternalContactValid(isValid);
    }

    function onNotificationUpcomingVisitChange(event) {
        let checked = event.target.checked;
        setExternalContact((prevExternalContact) => {
            return {
                ...prevExternalContact,
                notificationUpcomingVisit: checked
            }
        });
    }

    function onNotificationUpcomingVisitAlterationChange(event) {
        let checked = event.target.checked;
        setExternalContact((prevExternalContact) => {
            return {
                ...prevExternalContact,
                notificationUpcomingVisitAlteration: checked
            }
        });
    }

    function deleteLocation(locationExternalContactId) {
        setExternalContact((prevExternalContact) => {
            return {
                ...prevExternalContact,
                locations: prevExternalContact.locations.filter(function (location) { return location.locationExternalContactId !== locationExternalContactId }),
                LocationExternalContactIdsToDelete: [...prevExternalContact.LocationExternalContactIdsToDelete, locationExternalContactId]
            }
        });
    }

    function onSave() {
        onExternalContactSave(externalContactValue).then(
            (successMessage) => {
                NotificationToaster.show(Intent.SUCCESS, successMessage);
                if (editing) {
                    setOriginalValues(_.extend({}, {
                        ...externalContactValue,
                        LocationExternalContactIdsToDelete: []
                    }));
                    setExternalContact((prevExternalContact) => {
                        return {
                            ...prevExternalContact,
                            LocationExternalContactIdsToDelete: []
                        }
                    });
                } else {
                    setExternalContact(externalContactModel);
                    if (inputRef.current != null) {
                        inputRef.current.focus();
                    }
                }
            },
            (error) => {
                NotificationToaster.show(Intent.DANGER, error);
            });
    }

    useEffect(() => {
        let data = [];
        if (externalContactValue.locations != null && externalContactValue.locations.length > 0) {
            data = externalContactValue.locations.map((d) => {
                return [
                    d.name,
                    d.contactTypeName,
                    <div className="button-row-small" key={"actions-" + d.id}>
                        <Button disabled={!externalContactValue.verified} onClick={() => deleteLocation(d.locationExternalContactId)} icon="trash" iconOnly={true} text="Delete External Contact" large={false} minimal={true}></Button>
                    </div>];
            })
        }
        setTableRows(data);
    }, [externalContactValue.locations]);

    return (
        <div>
            <UserBasicSettings
                user={externalContactValue}
                loading={loading}
                editing={editing}
                onSettingsUpdate={onSettingsUpdate}
                saving={saving}
                ssoAccount={false}
                onValidationUpdate={onExternalContactValidationChange}
                pending={!externalContactValue.verified}
                externalUser={true}
                inputRef={inputRef}
            />
            <AlertUnsavedData
                isDirty={isDataDirty}>
            </AlertUnsavedData>
            <div className="form-field">
                <h4>Selected notifications:</h4>

                <FormSwitch
                    id="notification-upcoming-visit"
                    disabled={saving || !externalContactValue.verified}
                    label="Receive daily upcoming visit emails"
                    checked={externalContactValue.notificationUpcomingVisit}
                    onChange={onNotificationUpcomingVisitChange}
                    inline={true}
                    alignment={Alignment.END}
                    loading={loading}
                />
                <FormSwitch
                    id="notification-upcoming-visit-alteration"
                    disabled={saving || !externalContactValue.verified}
                    label="Receive visit notification emails"
                    checked={externalContactValue.notificationUpcomingVisitAlteration}
                    onChange={onNotificationUpcomingVisitAlterationChange}
                    inline={true}
                    alignment={Alignment.END}
                    loading={loading}
                />

                <ShowHide
                    evaluator={editing}
                    show={(
                        <Fragment>
                            <h4>
                                External Contact used at the following locations:
                            </h4>

                            <ListingTable
                                id="listing-table-location-contact"
                                headers={tableHeaders}
                                loadingData={loading}
                                data={tableRows}
                                totalRecordCount={externalContactValue.locations.length}
                                pageable={false}
                                noDataHeading={"This external contact has not been assigned to any locations"}
                            />
                        </Fragment>
                    )}
                />
                
            </div>
            <ShowHide
                evaluator={externalContactValue.verified}
                show={(
                    <ButtonSave
                        id="user-role-save"
                        onClick={onSave}
                        disabled={saving}
                        simpleDisabled={!externalContactValid}
                        loading={loading}
                    ></ButtonSave>
                )}
            />
        </div>
    );
}

ExternalContactSetting.propTypes = {
    externalContact: PropTypes.object,
    saving: PropTypes.bool,
    editing: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    onExternalContactSave: PropTypes.func.isRequired
};